import { Validators } from 'utils/validators';
import { FormModel } from 'components/Form';

const section = (field: string, label: string): FormModel => ({
  label,
  value: '',
  type: 'section',
  placeholder: '',
  field,
  validators: [],
  required: false,
  responsive: { xs: 12 },
  compactSection: true,
});

export const ActualRevenueForm = (
  strings: any,
  isWithinActualizationWindow: boolean,
  isTransferToRoom: boolean,
  serviceChargeDropdown: any,
  serviceChargeList: any,
  hasSplitRevenuePrivilege: boolean,
  revenueOwnerList: any[],
  bookedGuestsCount: number,
  bookedHallRental: number,
  bookedServiceChargePercent: number,
  excessGuestCount: number = 0,
  extraGuestRateText: string = '',
): FormModel[] => {
  const formFields: FormModel[] = [
    section('event_details_section', strings.actualization_event_details),
    {
      label: strings.booking_details_actual_revenue_number_of_guest,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      disabled: !isWithinActualizationWindow,
      placeholder: '',
      field: 'guaranteedGuests',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateInt, max: 100000 }],
      responsive: { xs: 12, sm: 6, md: 3 },
      sx: { pr: 2 },
      required: true,
      bookedBaseline: bookedGuestsCount,
      bookedVarianceFormat: 'guests',
    },
    {
      label: strings.booking_details_actual_revenue_billable_guests,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      disabled: true,
      placeholder: '',
      field: 'billableGuests',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 3 },
      sx: { pr: 2 },
      required: false,
      endChip: strings.actualization_billable_calculated,
      belowText: strings.actualization_billable_based_on,
    },
    {
      label: strings.booking_details_actual_revenue_excess_guest_rate,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      disabled: !isWithinActualizationWindow,
      placeholder: '',
      field: 'excessGuestRate',
      validators: [{ check: Validators.validateFloat, min: 0, max: 100 }],
      responsive: { xs: 12, sm: 6, md: 3 },
      sx: { pr: 2 },
      required: false,
      endAdornment: '%',
      belowText: extraGuestRateText
        ? String(strings.formatString(strings.actualization_excess_guest_rate_hint, extraGuestRateText))
        : '',
      belowTextRight:
        excessGuestCount > 0
          ? String(strings.formatString(strings.actualization_excess_guest_count_chip, excessGuestCount))
          : strings.actualization_no_change,
      belowTextRightTone: excessGuestCount > 0 ? 'positive' : 'neutral',
    },
    {
      label: strings.new_booking_service_charge,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      disabled: !isWithinActualizationWindow,
      variant: 'outlined',
      placeholder: '',
      field: 'serviceChargePercentage',
      validators: [{ check: Validators.validateFloat, min: 0, max: 100 }],
      responsive: { xs: 12, sm: 6, md: 3 },
      sx: { pr: 2 },
      required: false,
      endAdornment: '%',
      bookedBaseline: bookedServiceChargePercent,
      bookedVarianceFormat: 'percent',
    },
    {
      label: strings.bookingHistoryTabsPricePerPerson,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      disabled: true,
      variant: 'outlined',
      placeholder: '',
      field: 'pricePerPerson',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 3 },
      sx: { pr: 2 },
      required: false,
      endChip: strings.actualization_billable_calculated,
    },
    {
      label: strings.bookingHistoryTabsMinimumRevenue,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      disabled: true,
      variant: 'outlined',
      placeholder: '',
      field: 'minimumRevenue',
      validators: [{ check: Validators.validateFloat }],
      responsive: { xs: 12, sm: 6, md: 3 },
      sx: { pr: 2 },
      required: false,
      endChip: strings.actualization_billable_calculated,
    },
    {
      label: strings.bookingDetailsHallRental,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      disabled: !isWithinActualizationWindow,
      variant: 'outlined',
      placeholder: '',
      field: 'hallRental',
      validators: [{ check: Validators.validateFloat }],
      responsive: { xs: 12, sm: 6, md: 3 },
      sx: { pr: 2 },
      required: false,
      bookedBaseline: bookedHallRental,
      bookedVarianceFormat: 'currency',
    },
    section('settlement_section', strings.actualization_settlement),
    {
      label: strings.actualization_isTransferToRoom,
      value: false,
      size: 'medium',
      selected: isTransferToRoom,
      type: 'checkbox',
      field: 'isTransferToRoom',
      disabled: !isWithinActualizationWindow,
      responsive: { xs: 12, sm: 6, md: 3 },
      placeholder: '',
      validators: [],
      required: false,
    },
  ];

  if (isTransferToRoom) {
    formFields.push(
      {
        label: strings.actualization_roomNumber,
        value: '',
        size: 'medium',
        type: 'text',
        typeValue: 'text',
        variant: 'outlined',
        disabled: !isWithinActualizationWindow,
        placeholder: '',
        field: 'roomNumber',
        validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateString, max: 50 }],
        responsive: { xs: 12, sm: 6, md: 3 },
        required: true,
        sx: { pr: 2 },
      },
      {
        label: strings.actualization_reservation_name,
        value: '',
        size: 'medium',
        type: 'text',
        typeValue: 'text',
        variant: 'outlined',
        placeholder: '',
        field: 'roomReservationName',
        validators: [],
        responsive: { xs: 12, sm: 6, md: 3 },
        required: false,
        disabled: true,
        sx: { pr: 2 },
      },
      {
        label: strings.actualization_reservation_id,
        value: '',
        size: 'medium',
        type: 'text',
        typeValue: 'text',
        variant: 'outlined',
        placeholder: '',
        field: 'roomReservationId',
        validators: [],
        responsive: { xs: 12, sm: 6, md: 3 },
        required: false,
        disabled: true,
        sx: { pr: 2 },
      },
    );
  }

  formFields.push(section('accounting_section', strings.actualization_accounting));

  if (serviceChargeDropdown) {
    const idx = formFields.findIndex((i: any) => i.field === 'serviceChargePercentage');
    if (idx >= 0) {
      formFields[idx] = {
        label: strings.new_booking_service_charge,
        value: {},
        size: 'medium',
        type: 'drop-down',
        placeholder: `${strings.new_booking_service_charge} `,
        field: 'serviceCharge',
        validators: [],
        responsive: { sm: 6, xs: 12, md: 3 },
        required: false,
        options: serviceChargeList,
        sx: { mr: 2 },
        bookedBaseline: bookedServiceChargePercent,
        bookedVarianceFormat: 'percent',
      };
    }
  }

  formFields.push({
    label: strings.revenue_owner_title_list,
    value: [],
    size: 'medium',
    type: 'select',
    typeValue: 'text',
    isMulti: true,
    placeholder: '',
    field: 'revenueOwners',
    validators: [],
    required: false,
    responsive: { xs: 12, sm: 6, md: 6 },
    sx: { pr: 2 },
    options: revenueOwnerList,
    disabled: !isWithinActualizationWindow,
  });

  return formFields;
};
